import React, { useEffect, useState, useCallback } from 'react';
import { dealMgmt } from '../../services/dealManagementApi';
import { KpiCard, Card, CardHead } from '../../components/ui';
import { LoadingState, EmptyState, ErrorState } from '../../features/shared/PageStates';

const DASHBOARD_CARDS = [
  { key: 'total_active', label: 'Total active', filter: null },
  { key: 'new_leads', label: 'New leads', filter: { stage: 'new_lead' } },
  { key: 'stage_1_vetting', label: 'Stage 1', filter: { stage: 'stage_1_vetting' } },
  { key: 'awaiting_ic', label: 'Awaiting IC', filter: { stage: 'ic_review' } },
  { key: 'stage_2_dd', label: 'Stage 2 DD', filter: { stage: 'stage_2_dd' } },
  { key: 'contracting', label: 'Contracting', filter: { stage: 'contracting' } },
  { key: 'team_lead_qc', label: 'Team Lead QC', filter: { stage: 'team_lead_qc' } },
  { key: 'ready_for_finance', label: 'Ready for finance', filter: { stage: 'finance_handover' } },
  { key: 'supplier_payment', label: 'Supplier payment', filter: { stage: 'supplier_payment' } },
  { key: 'repayment_monitoring', label: 'Repayment', filter: { stage: 'repayment_monitoring' } },
  { key: 'above_60_days', label: 'Above 60 days', filter: { risk: 'high' } },
  { key: 'overdue', label: 'Overdue actions', filter: { overdue: '1' } },
];

const RISK_CARDS = [
  { key: 'deals_requiring_action', label: 'Requiring action', filter: { action: '1' } },
  { key: 'below_30_margin', label: 'Below 30% margin', filter: { below_margin: '1' } },
  { key: 'above_75_limit', label: 'Above 75% advance', filter: { above_75: '1' } },
  { key: 'end_client_amber_red', label: 'End-client Amber/Red', filter: { rating: 'amber_red' } },
  { key: 'director_possible_match', label: 'Director match', filter: { director: 'match' } },
  { key: 'closed_this_month', label: 'Closed this month', filter: { stage: 'closed' } },
];

export function DealDashboard({ onNavigate, showToast, user }) {
  const [cards, setCards] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    dealMgmt.dashboard()
      .then(res => setCards(res.cards || res || {}))
      .catch(e => {
        setError(e.message || 'Failed to load dashboard');
        showToast?.(e.message, 'error');
      })
      .finally(() => setLoading(false));
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const goPipeline = (filter = {}) => onNavigate('dm-pipeline', { dmFilter: filter });

  if (loading) return <LoadingState label="Loading ops dashboard…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const values = Object.values(cards).filter(v => typeof v === 'number');
  const isEmpty = values.length > 0 && values.every(v => !v);

  return (
    <div>
      {isEmpty && (
        <EmptyState
          title="No operational deals yet"
          sub="Create a deal to start the workflow."
          action={() => onNavigate('dm-new-deal')}
          actionLabel="+ New deal"
        />
      )}

      <div className="kpi-grid g6">
        {DASHBOARD_CARDS.map(c => {
          const val = cards[c.key] ?? 0;
          return (
            <div
              key={c.key}
              onClick={() => c.filter != null && goPipeline(c.filter)}
              style={{ cursor: c.filter != null ? 'pointer' : 'default' }}
            >
              <KpiCard
                label={c.label}
                value={val}
                highlight={c.key === 'total_active' || c.key === 'overdue'}
                highlightColor={c.key === 'overdue' ? 'accent' : 'brand'}
              />
            </div>
          );
        })}
      </div>

      <div className="deal-dashboard-split" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12 }}>
        <Card>
          <CardHead title="Risk & action items" sub="Click a metric to open the filtered pipeline" />
          <div className="card-body">
            <div className="kpi-grid g3" style={{ marginBottom: 0 }}>
              {RISK_CARDS.map(c => (
                <div key={c.key} onClick={() => goPipeline(c.filter || {})} style={{ cursor: 'pointer' }}>
                  <KpiCard
                    label={c.label}
                    value={cards[c.key] ?? 0}
                    highlight={(cards[c.key] ?? 0) > 0}
                    highlightColor="accent"
                  />
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card>
          <CardHead title="Quick actions" />
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button className="btn btn-p" onClick={() => onNavigate('dm-pipeline')}>View pipeline</button>
            <button className="btn btn-o" onClick={() => onNavigate('dm-new-deal')}>+ New deal</button>
            <button className="btn btn-o" onClick={() => goPipeline({ mine: '1', deal_manager_id: user?.id })}>My deals</button>
            <button className="btn btn-o" onClick={() => onNavigate('dm-ic')}>IC queue</button>
            <button className="btn btn-o" onClick={() => onNavigate('dm-finance')}>Finance queue</button>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default DealDashboard;
